import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { api, goalsAPI, trainingPreferencesAPI } from '../../api';
import { useTranslation } from '../../i18n';
import { toast } from '../common/Notifier';
import { DatePicker } from './DatePicker';
import { COUNTRIES, MIN_DAYS_BETWEEN_RACES, WEEKDAY_KEYS } from '../../constants/preferences';
import { DISTANCE_PRESETS } from '../../constants/races';
import { field, primaryBtn, sectionClass } from './styles';

function parseToDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

const toIso = (d) => (typeof d === 'string' ? d : d.toISOString().slice(0, 10));
const emptyGoals = () => ({
  A: { distance_m: '', date: null },
  B: { distance_m: '', date: null },
  C: { distance_m: '', date: null },
});
const PRIORITIES = ['A', 'B', 'C'];

export const TrainingTab = ({ athlete, highlightedId }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const hasCheckedControlGoal = useRef(false);

  // --- Неделя ---
  const [intervalsPerWeek, setIntervalsPerWeek] = useState(2);
  const [longRunWeekday, setLongRunWeekday] = useState(6);
  const [mpSegmentMode, setMpSegmentMode] = useState('always');
  const [restWeekdays, setRestWeekdays] = useState([]);
  const [warmupKm, setWarmupKm] = useState(3.0);
  const [cooldownKm, setCooldownKm] = useState(2.0);
  const [longRunWarmupKm, setLongRunWarmupKm] = useState(0.0);
  const [longRunCooldownKm, setLongRunCooldownKm] = useState(0.0);
  const [savingWeek, setSavingWeek] = useState(false);

  // --- Город / страна ---
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [cityError, setCityError] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [showCountryInput, setShowCountryInput] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);

  // --- Старты ---
  const [goals, setGoals] = useState(emptyGoals);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [savingGoals, setSavingGoals] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const priorityLabels = useMemo(() => ({
    A: t('preferences.goal_a'),
    B: t('preferences.goal_b'),
    C: t('preferences.goal_c'),
  }), [t]);

  const warmupFields = [
    ['preferences.warmup_km', warmupKm, setWarmupKm],
    ['preferences.cooldown_km', cooldownKm, setCooldownKm],
    ['preferences.long_run_warmup', longRunWarmupKm, setLongRunWarmupKm],
    ['preferences.long_run_cooldown', longRunCooldownKm, setLongRunCooldownKm],
  ];

  // ---------- загрузка ----------
  const loadPreferences = useCallback(async () => {
    if (!athlete?.id) return;
    try {
      const { data } = await trainingPreferencesAPI.get(athlete.id);
      setIntervalsPerWeek(data.intervals_per_week ?? 2);
      setLongRunWeekday(data.long_run_weekday ?? 6);
      setRestWeekdays(data.rest_weekdays ?? []);
      setWarmupKm(data.warmup_km ?? 3.0);
      setCooldownKm(data.cooldown_km ?? 2.0);
      setLongRunWarmupKm(data.long_run_warmup_km ?? 0.0);
      setLongRunCooldownKm(data.long_run_cooldown_km ?? 0.0);
      setMpSegmentMode(data.mp_segment_mode ?? 'always');
      setCity(data.city ?? '');
      setCountry(data.country ?? '');
    } catch {
      // значения по умолчанию
    }
  }, [athlete?.id]);

  const loadGoals = useCallback(async () => {
    if (!athlete?.id) return;
    try {
      const { data } = await goalsAPI.list({ athlete_id: athlete.id, status: 'planned' });
      const next = emptyGoals();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      data
        .filter((g) => g.status === 'planned' && parseToDate(g.date) >= today)
        .forEach((g) => {
          if (g.priority in next) {
            next[g.priority] = { distance_m: g.distance_m || '', date: g.date || null };
          }
        });
      setGoals(next);
    } catch {
      toast.error(tRef.current('error.general'));
    } finally {
      setGoalsLoading(false);
    }
  }, [athlete?.id]);

  useEffect(() => {
    if (athlete?.id) {
      loadPreferences();
      loadGoals();
    }
  }, [athlete?.id, loadPreferences, loadGoals]);

  const hasUpcoming = useCallback((g) => {
    const now = new Date();
    const limit = new Date(now.getTime() + 8 * 7 * 24 * 60 * 60 * 1000);
    return PRIORITIES.some((p) => {
      const d = parseToDate(g[p]?.date);
      return d && d >= now && d <= limit;
    });
  }, []);

  // Если в ближайшие 8 недель стартов нет — предлагаем контрольный C через 4 недели
  useEffect(() => {
    if (goalsLoading || hasCheckedControlGoal.current) return;

    if (goals.C?.distance_m && goals.C?.date) {
      hasCheckedControlGoal.current = true;
      return;
    }
    if (!hasUpcoming(goals)) {
      const d = new Date();
      d.setDate(d.getDate() + 28);
      setGoals((prev) => ({ ...prev, C: { distance_m: 5000, date: d.toISOString().slice(0, 10) } }));
      hasCheckedControlGoal.current = true;
    }
  }, [goals, goalsLoading, hasUpcoming]);

  // ---------- сохранение ----------
  // и «неделя», и «город» шлют один и тот же объект настроек
  const prefsPayload = () => ({
    athlete_id: athlete.id,
    city,
    country,
    intervals_per_week: intervalsPerWeek,
    long_run_weekday: longRunWeekday,
    rest_weekdays: restWeekdays,
    warmup_km: warmupKm,
    cooldown_km: cooldownKm,
    long_run_warmup_km: longRunWarmupKm,
    long_run_cooldown_km: longRunCooldownKm,
    mp_segment_mode: mpSegmentMode,
  });

  const reportSaveError = (error) => {
    const data = error.response?.data;
    if (data?.error === 'city_not_found') {
      const msg = t('preferences.city_not_found_with_country', {
        city: data?.city || city,
        country: data?.country || country,
      });
      setCityError(msg);
      toast.error(msg);
    } else {
      toast.error(data?.message || data?.error || t('error.general'));
    }
  };

  const handleSaveLocation = async (e) => {
    e.preventDefault();
    setCityError('');

    if (city && !country) {
      setCityError(t('preferences.country_required'));
      toast.error(t('preferences.country_required'));
      return;
    }

    setSavingLocation(true);
    try {
      await api.post('/training-preferences/', prefsPayload());
      toast.success(t('preferences.location_saved'));
      await loadPreferences();
    } catch (error) {
      reportSaveError(error);
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSaveWeekSettings = async (e) => {
    e.preventDefault();
    setSavingWeek(true);
    try {
      const { data } = await api.post('/training-preferences/', prefsPayload());
      if (data.schedule_changed) toast.info(t('preferences.schedule_reset_notice'));
      else toast.success(t('common.success'));
      await loadPreferences();
    } catch (error) {
      reportSaveError(error);
    } finally {
      setSavingWeek(false);
    }
  };

  const updateGoal = (priority, key, value) =>
    setGoals((prev) => ({ ...prev, [priority]: { ...prev[priority], [key]: value } }));

  const toggleRestDay = (day) =>
    setRestWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

  const handleSaveGoals = async (e) => {
    e.preventDefault();

    const filled = PRIORITIES
      .filter((p) => goals[p]?.distance_m && goals[p]?.date)
      .map((p) => ({ priority: p, dateObj: parseToDate(goals[p].date) }));

    if (filled.length === 0) {
      toast.error(t('preferences.no_goals_filled'));
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (const g of filled) {
      if (!g.dateObj || g.dateObj < today) {
        toast.error(`${t('preferences.date_in_past')} (${g.priority})`);
        return;
      }
    }

    for (let i = 0; i < filled.length; i++) {
      for (let j = i + 1; j < filled.length; j++) {
        const diffDays = Math.abs((filled[i].dateObj - filled[j].dateObj) / 86400000);
        if (diffDays < MIN_DAYS_BETWEEN_RACES) {
          toast.error(`${t('preferences.races_too_close')} (${filled[i].priority} / ${filled[j].priority})`);
          return;
        }
      }
    }

    setSavingGoals(true);
    try {
      await goalsAPI.deleteAll({ athlete_id: athlete.id });
      for (const [priority, data] of Object.entries(goals)) {
        if (data.distance_m && data.date) {
          await goalsAPI.create({
            athlete_id: athlete.id,
            distance_m: parseFloat(data.distance_m),
            date: toIso(data.date),
            priority,
            status: 'planned',
          });
        }
      }
      toast.success(t('preferences.goals_saved'));
      await loadGoals();
    } catch (error) {
      console.error('Save goals error:', error);
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingGoals(false);
    }
  };

  const countryBorder = cityError ? 'border-red-500' : 'border-gray-300';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Ряд 1: неделя | старты, ряд 2: локация на всю ширину */}
      <form id="week-section" onSubmit={handleSaveWeekSettings} className={sectionClass('week-section', highlightedId)}>
        <h3 className="text-lg font-semibold text-gray-800">📅 {t('preferences.week_title')}</h3>

        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('preferences.intervals_per_week')}</label>
          <select value={intervalsPerWeek} onChange={(e) => setIntervalsPerWeek(parseInt(e.target.value, 10))} className={field}>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('preferences.long_run_day')}</label>
          <select value={longRunWeekday} onChange={(e) => setLongRunWeekday(parseInt(e.target.value, 10))} className={field}>
            {WEEKDAY_KEYS.map((d) => (
              <option key={d.value} value={d.value}>{t(`preferences.weekday_${d.key}`)}</option>
            ))}
          </select>
        </div>

        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('preferences.mp_segment_mode_label')}</label>
          <select value={mpSegmentMode} onChange={(e) => setMpSegmentMode(e.target.value)} className={field}>
            <option value="always">{t('preferences.mp_segment_always')}</option>
            <option value="never">{t('preferences.mp_segment_never')}</option>
            <option value="random">{t('preferences.mp_segment_random')}</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">{t('preferences.mp_segment_hint')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('preferences.rest_days')}</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_KEYS.map((d) => (
              <button
                type="button"
                key={d.value}
                onClick={() => toggleRestDay(d.value)}
                aria-pressed={restWeekdays.includes(d.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  restWeekdays.includes(d.value) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(`preferences.weekday_${d.key}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="text-sm font-medium text-gray-700">🏃 {t('preferences.warmup_cooldown_title')}</div>
          <div className="grid grid-cols-2 gap-3">
            {warmupFields.map(([labelKey, value, setter]) => (
              <div key={labelKey}>
                <label className="block text-xs text-gray-500 mb-1">{t(labelKey)}</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => setter(parseFloat(e.target.value) || 0)}
                  className={field}
                />
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={savingWeek} className={primaryBtn}>
          {savingWeek ? t('common.loading') : t('preferences.save')}
        </button>
      </form>

      <form id="goals-section" onSubmit={handleSaveGoals} className={sectionClass('goals-section', highlightedId)}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">🎯 {t('preferences.goals_title')}</h3>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTooltip(!showTooltip)}
                aria-label="?"
                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 font-bold text-sm flex items-center justify-center transition-all"
              >
                ?
              </button>

              {showTooltip && (
                <div className="absolute right-0 top-10 w-72 max-w-[calc(100vw-3rem)] bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 text-left">
                  <button type="button" onClick={() => setShowTooltip(false)} className="float-right text-gray-400 hover:text-gray-600">✕</button>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-semibold text-green-600">🟢 {priorityLabels.C}</span>
                      <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_c_info')}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-blue-600">🔵 {priorityLabels.B}</span>
                      <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_b_info')}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-red-600">🔴 {priorityLabels.A}</span>
                      <p className="text-gray-600 text-xs mt-1">{t('preferences.goal_a_info')}</p>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs text-gray-500">💡 {t('preferences.goal_auto_info')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-500">{t('preferences.goals_desc')}</p>

          {PRIORITIES.map((priority) => (
            <div key={priority} className="grid grid-cols-2 gap-3 items-end border-t pt-3 first:border-t-0 first:pt-0">
              <div className="col-span-2 text-sm font-medium text-gray-700">{priorityLabels[priority]}</div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('preferences.distance')}</label>
                <input
                  type="number"
                  list={`distance-presets-${priority}`}
                  value={goals[priority]?.distance_m || ''}
                  onChange={(e) => updateGoal(priority, 'distance_m', e.target.value)}
                  placeholder="10000"
                  className={field}
                />
                <datalist id={`distance-presets-${priority}`}>
                  {DISTANCE_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('preferences.date')}</label>
                <DatePicker
                  value={goals[priority]?.date || null}
                  onChange={(isoDate) => {
                    if (isoDate === null) {
                      setGoals((prev) => ({ ...prev, [priority]: { distance_m: '', date: null } }));
                    } else {
                      updateGoal(priority, 'date', isoDate);
                    }
                  }}
                  placeholder={t('preferences.date_placeholder')}
                />
              </div>
            </div>
          ))}

          {!hasUpcoming(goals) && !goals.C?.date && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              💡 {t('preferences.goal_auto_notice')}
            </div>
          )}

          <button
            type="submit"
            disabled={savingGoals}
            className="w-full mt-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
          >
            {savingGoals ? t('common.loading') : t('preferences.save')}
          </button>
        </form>

        <form id="location-section" onSubmit={handleSaveLocation} className={`${sectionClass('location-section', highlightedId)} md:col-span-2`}>
          <h3 className="text-lg font-semibold text-gray-800">📍 {t('preferences.city_title')}</h3>
          <p className="text-sm text-gray-500">{t('preferences.city_desc')}</p>

          <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="md:flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('preferences.city')}</label>
            <div className="flex gap-3">
              <input
                type="text"
                value={city}
                onChange={(e) => { setCity(e.target.value); setCityError(''); }}
                placeholder={t('preferences.city_placeholder')}
                className={`flex-1 min-w-0 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${countryBorder}`}
              />

              <div className="relative w-32 shrink-0">
                {!showCountryInput ? (
                  <select
                    value={country}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setShowCountryInput(true);
                        setCountryInput(country || '');
                      } else {
                        setCountry(e.target.value);
                        setCityError('');
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${countryBorder}`}
                  >
                    <option value="">{t('preferences.country')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                    <option value="__custom__">✏️ {t('preferences.other_country')}</option>
                  </select>
                ) : (
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={countryInput}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase();
                        setCountryInput(v);
                        setCountry(v);
                        setCityError('');
                      }}
                      placeholder={t('preferences.country_code')}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase ${countryBorder}`}
                      maxLength={2}
                    />
                    <button
                      type="button"
                      onClick={() => { setShowCountryInput(false); setCountryInput(''); }}
                      aria-label="✕"
                      className="px-2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-1">{t('preferences.city_hint')}</p>
            {cityError && <p className="text-sm text-red-600 mt-1">⚠️ {cityError}</p>}
          </div>

          <button type="submit" disabled={savingLocation}
                  className={`${primaryBtn} md:w-auto md:px-8 md:mt-6 md:whitespace-nowrap`}>
            {savingLocation ? t('common.loading') : `💾 ${t('preferences.save_location')}`}
          </button>
          </div>
        </form>
    </div>
  );
};